import { TrendingUp, Award, AlertTriangle, BarChart2 } from "lucide-react";

function notaCor(n) { return n >= 8 ? "#10b981" : n >= 6 ? "#f59e0b" : "#ef4444"; }


export default function Stats({ avaliacoes }) {
  if (avaliacoes.length === 0) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", flexDirection: "column", gap: 12, color: "var(--text3)" }}>
      <BarChart2 size={48} style={{ opacity: 0.3 }} />
      <div style={{ fontWeight: 500 }}>Nenhuma avaliação ainda</div>
      <div style={{ fontSize: 13 }}>As estatísticas aparecerão após a primeira avaliação</div>
    </div>
  );

  const notas = avaliacoes.map(a => parseFloat(a.nota)).filter(Boolean);
  const media = (notas.reduce((s, n) => s + n, 0) / notas.length).toFixed(2);
  const maior = Math.max(...notas).toFixed(1);
  const menor = Math.min(...notas).toFixed(1);
  const altos = notas.filter(n => n >= 8).length;
  const medios = notas.filter(n => n >= 6 && n < 8).length;
  const baixos = notas.filter(n => n < 6).length;
  const total = notas.length;

  const porEdital = {};
  avaliacoes.forEach(a => {
    if (!porEdital[a.edital]) porEdital[a.edital] = [];
    porEdital[a.edital].push(parseFloat(a.nota));
  });

  const dist = {};
  notas.forEach(n => {
    const faixa = Math.floor(n);
    dist[faixa] = (dist[faixa] || 0) + 1;
  });
  const maxDist = Math.max(...Object.values(dist));

  return (
    <div style={{ height: "100vh", overflowY: "auto", background: "var(--bg)" }}>
      <div style={{ background: "var(--bg2)", borderBottom: "1px solid var(--border)", padding: "16px 28px", boxShadow: "var(--shadow)" }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>Estatísticas</div>
        <div style={{ fontSize: 12, color: "var(--text3)" }}>Análise de {total} avaliações realizadas</div>
      </div>

      <div style={{ padding: "24px 28px", maxWidth: 900, margin: "0 auto" }}>
        {/* Cards principais */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
          {[
            { label: "Média geral", valor: media, icon: TrendingUp, cor: "var(--primary)", bg: "#eef2ff" },
            { label: "Maior nota", valor: maior, icon: Award, cor: "#10b981", bg: "#d1fae5" },
            { label: "Menor nota", valor: menor, icon: AlertTriangle, cor: "#ef4444", bg: "#fee2e2" },
            { label: "Total", valor: total, icon: BarChart2, cor: "#6366f1", bg: "#eef2ff" },
          ].map(({ label, valor, icon: Icon, cor, bg }) => (
            <div key={label} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 14, padding: "18px 20px", boxShadow: "var(--shadow)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: "var(--text3)", fontWeight: 500 }}>{label}</span>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon size={16} color={cor} />
                </div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: cor }}>{valor}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          {/* Distribuição */}
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 14, padding: "20px", boxShadow: "var(--shadow)" }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Distribuição por faixa</div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 16 }}>Quantidade de projetos por nota</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[9,8,7,6,5,4,3,2,1,0].map(faixa => {
                const qtd = dist[faixa] || 0;
                const pct = maxDist ? (qtd / maxDist) * 100 : 0;
                return (
                  <div key={faixa} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: notaCor(faixa + 0.5), width: 24, textAlign: "right" }}>{faixa}</span>
                    <div style={{ flex: 1, height: 20, background: "var(--bg3)", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: notaCor(faixa + 0.5), borderRadius: 4, transition: "width .5s", minWidth: qtd > 0 ? 4 : 0 }} />
                    </div>
                    <span style={{ fontSize: 12, color: "var(--text3)", width: 20 }}>{qtd}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Pizza simplificada */}
            <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 14, padding: "20px", boxShadow: "var(--shadow)" }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Resultado geral</div>
              <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 14 }}>Classificação dos projetos</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { label: "Aprovados com destaque (≥8)", qtd: altos, cor: "#10b981", bg: "#d1fae5" },
                  { label: "Aprovados (6–8)", qtd: medios, cor: "#f59e0b", bg: "#fef3c7" },
                  { label: "Necessitam revisão (<6)", qtd: baixos, cor: "#ef4444", bg: "#fee2e2" },
                ].map(({ label, qtd, cor, bg }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: 1, height: 28, background: "var(--bg3)", borderRadius: 6, overflow: "hidden", position: "relative" }}>
                      <div style={{ width: `${(qtd/total)*100}%`, height: "100%", background: bg, transition: "width .5s" }} />
                      <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "var(--text2)", fontWeight: 500 }}>{label}</span>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: cor, width: 28, textAlign: "right" }}>{qtd}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Por edital */}
            <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 14, padding: "20px", boxShadow: "var(--shadow)", flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Média por edital</div>
              <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 14 }}>Desempenho histórico</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {Object.entries(porEdital).map(([edital, ns]) => {
                  const med = (ns.reduce((s,n)=>s+n,0)/ns.length);
                  return (
                    <div key={edital} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 12, color: "var(--text2)", width: 100, flexShrink: 0 }}>{edital}</span>
                      <div style={{ flex: 1, height: 6, background: "var(--bg3)", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${(med/10)*100}%`, height: "100%", background: notaCor(med), borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: notaCor(med), width: 30, textAlign: "right" }}>{med.toFixed(1)}</span>
                      <span style={{ fontSize: 11, color: "var(--text3)", width: 20 }}>({ns.length})</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
